import React, { useState, useMemo } from "react";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

const BotMessageType = {
    OTP: "OTP",
    PROMOTIONAL: "Promotional",
    TRANSACTIONAL: "Transactional",
};

const SearchableBotDropdown = ({
    value,
    onChange,
    onBlur,
    disabled = false,
    error,
    helperText,
    botList = [],
    label = "Select Bot",
    labelId = "select-bot-label",
    id = "select-bot",
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [open, setOpen] = useState(false);

    const filteredBots = useMemo(() => {
        if (!searchTerm) return botList;

        return botList.filter(bot =>
            bot.botName?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [botList, searchTerm]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSearchTerm("");
    };

    return (
        <FormControl fullWidth>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Select
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                labelId={labelId}
                id={id}
                value={value ?? ""}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                error={Boolean(error)}
                label={label}
                fullWidth
                MenuProps={{
                    PaperProps: {
                        sx: {
                            overflow: "scroll",
                            maxHeight: "40%",
                            boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        },
                    },
                }}
            >
                {/* Search Field */}
                <Box sx={{ px: 2, py: 1, position: "sticky", top: 0, backgroundColor: "white", zIndex: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search bots"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#f5f5f5",
                            },
                        }}
                    />
                </Box>

                {filteredBots.length > 0 ? (
                    filteredBots.map((bot, index) => (
                        <MenuItem key={index} value={bot}>
                            <Grid container justifyContent={"space-between"} alignItems="center">
                                <Grid item display="flex" gap={2} md={8}>
                                    {bot.botLogoImage?.url ? (
                                        <img
                                            src={bot.botLogoImage.url}
                                            height={"20px"}
                                            width={"20px"}
                                            style={{ objectFit: "scale-down" }}
                                            alt="bot logo"
                                        />
                                    ) : (
                                        <SmartToyOutlinedIcon />
                                    )}
                                    <Typography variant="h6">{bot.botName}</Typography>
                                </Grid>
                                <Grid item md={4}>
                                    <Typography variant="body1">
                                        Type:{" "}
                                        {(() => {
                                            const type = bot.botMessageType?.toUpperCase();
                                            switch (type) {
                                                case "OTP":
                                                    return <b>{BotMessageType.OTP}</b>;
                                                case "PROMOTIONAL":
                                                    return <b>{BotMessageType.PROMOTIONAL}</b>;
                                                case "TRANSACTIONAL":
                                                    return <b>{BotMessageType.TRANSACTIONAL}</b>;
                                                default:
                                                    return "NA";
                                            }
                                        })()}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled>
                        <Typography variant="h6">No bots found</Typography>
                    </MenuItem>
                )}
            </Select>
            {helperText && (
                <Typography color="error" variant="caption">
                    {helperText}
                </Typography>
            )}
        </FormControl>
    );
};

export default SearchableBotDropdown;
